import { ApolloError } from '@apollo/client';

import { getDefaultErrorMessage } from '@/core/apiErrorMessages';

// Error codes from the OTP implementation plan
export const OTP_ERROR_CODES = {
  // OTP Flow Errors
  OTP_RATE_LIMIT_EXCEEDED: 'OTP_RATE_LIMIT_EXCEEDED',
  OTP_IP_RATE_LIMIT_EXCEEDED: 'OTP_IP_RATE_LIMIT_EXCEEDED',
  OTP_EXPIRED: 'OTP_EXPIRED',
  OTP_INVALID: 'OTP_INVALID',
  OTP_MAX_ATTEMPTS_EXCEEDED: 'OTP_MAX_ATTEMPTS_EXCEEDED',
  OTP_SEND_FAILED: 'OTP_SEND_FAILED',
  INVALID_OTP_TOKEN: 'INVALID_OTP_TOKEN',
  OTP_TOKEN_EXPIRED: 'OTP_TOKEN_EXPIRED',

  // Signup Flow Errors
  PHONE_ALREADY_REGISTERED: 'PHONE_ALREADY_REGISTERED',
  SETUP_INTENT_NOT_COMPLETED: 'SETUP_INTENT_NOT_COMPLETED',
  UNAUTHORIZED: 'UNAUTHORIZED',

  // Authentication Errors
  AUTH_INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  AUTH_LOCKED_FOR_INTERVAL: 'AUTH_LOCKED_FOR_INTERVAL',
  AUTH_USER_NOT_FOUND: 'AUTH_USER_NOT_FOUND',

  // Phone Linking Errors
  PHONE_ALREADY_LINKED: 'PHONE_ALREADY_LINKED',
  PHONE_INVALID_FORMAT: 'PHONE_INVALID_FORMAT',
  PHONE_UNSUPPORTED_REGION: 'PHONE_UNSUPPORTED_REGION',

  // Account Errors
  EMAIL_ALREADY_EXISTS: 'EMAIL_ALREADY_EXISTS',
  USER_ALREADY_EXISTS: 'USER_ALREADY_EXISTS',
  ADDRESS_NOT_VERIFIED: 'ADDRESS_NOT_VERIFIED',
} as const;

// User-friendly error messages for each error code
export const OTP_ERROR_MESSAGES: Record<string, string> = {
  // OTP Flow Errors
  [OTP_ERROR_CODES.OTP_RATE_LIMIT_EXCEEDED]:
    'Too many verification attempts. Please wait 10 minutes before trying again.',
  [OTP_ERROR_CODES.OTP_IP_RATE_LIMIT_EXCEEDED]:
    'Too many verification attempts. Please wait 10 minutes before trying again.',
  [OTP_ERROR_CODES.OTP_EXPIRED]: 'This code has expired. Please request a new one.',
  [OTP_ERROR_CODES.OTP_INVALID]: 'Invalid code. Please check and try again.',
  [OTP_ERROR_CODES.OTP_MAX_ATTEMPTS_EXCEEDED]:
    'Too many incorrect attempts. Please request a new code.',
  [OTP_ERROR_CODES.OTP_SEND_FAILED]: 'Unable to send verification code. Please try again.',
  [OTP_ERROR_CODES.INVALID_OTP_TOKEN]: 'Session expired. Please verify your phone again.',
  [OTP_ERROR_CODES.OTP_TOKEN_EXPIRED]: 'Session expired. Please verify your phone again.',

  // Signup Flow Errors
  [OTP_ERROR_CODES.PHONE_ALREADY_REGISTERED]: 'This phone number is already registered.',
  [OTP_ERROR_CODES.SETUP_INTENT_NOT_COMPLETED]: 'Please complete payment setup first.',
  [OTP_ERROR_CODES.UNAUTHORIZED]: 'Unauthorized access.',

  // Authentication Errors
  [OTP_ERROR_CODES.AUTH_INVALID_CREDENTIALS]: 'Incorrect password. Please try again.',
  [OTP_ERROR_CODES.AUTH_LOCKED_FOR_INTERVAL]:
    'Account temporarily locked. Please try again in 24 hours.',
  [OTP_ERROR_CODES.AUTH_USER_NOT_FOUND]: 'No account found with this email address.',

  // Phone Linking Errors
  [OTP_ERROR_CODES.PHONE_ALREADY_LINKED]:
    'This phone number is already associated with another account.',
  [OTP_ERROR_CODES.PHONE_INVALID_FORMAT]: 'Please enter a valid phone number.',
  [OTP_ERROR_CODES.PHONE_UNSUPPORTED_REGION]: 'SMS is not available in your region.',

  // Account Errors
  [OTP_ERROR_CODES.EMAIL_ALREADY_EXISTS]: 'An account with this email already exists.',
  [OTP_ERROR_CODES.USER_ALREADY_EXISTS]: 'An account with this email already exists.',
  [OTP_ERROR_CODES.ADDRESS_NOT_VERIFIED]: 'This phone number is not verified.',
};

/**
 * Extract error code from an Apollo error
 */
export function getErrorCode(error: unknown): string | null {
  if (error instanceof ApolloError && error.graphQLErrors.length > 0) {
    const extensions = error.graphQLErrors[0]?.extensions;
    if (extensions?.code) {
      return extensions.code as string;
    }
  }
  return null;
}

/**
 * Get user-friendly error message from an error
 */
export function getErrorMessage(error: unknown): string {
  const code = getErrorCode(error);
  if (code && OTP_ERROR_MESSAGES[code]) {
    return OTP_ERROR_MESSAGES[code];
  }

  // The map above only covers this flow's own SCREAMING_SNAKE codes. Fall through to the
  // shared registry for the dotted API codes (billing.*, consultation.*) that the signup
  // mutations can also raise — otherwise the raw GraphQL string reaches the user (ENG-5571).
  if (code) {
    const sharedMessage = getDefaultErrorMessage(code);
    if (sharedMessage) return sharedMessage;
  }

  // Fallback to error message if available
  if (error instanceof Error) {
    return error.message || 'An unexpected error occurred. Please try again.';
  }

  return 'An unexpected error occurred. Please try again.';
}

/**
 * Check if an error has a specific error code
 */
export function hasErrorCode(error: unknown, code: string): boolean {
  if (error instanceof ApolloError) {
    return error.graphQLErrors.some((e) => e.extensions?.code === code);
  }
  return false;
}

/**
 * Check if the error indicates the OTP token has expired
 * (user needs to re-verify their phone)
 */
export function isOTPTokenExpired(error: unknown): boolean {
  return (
    hasErrorCode(error, OTP_ERROR_CODES.INVALID_OTP_TOKEN) ||
    hasErrorCode(error, OTP_ERROR_CODES.OTP_TOKEN_EXPIRED)
  );
}

/**
 * Check if the error indicates an email conflict
 * (email already exists, trigger email conflict flow)
 */
export function isEmailConflict(error: unknown): boolean {
  return (
    hasErrorCode(error, OTP_ERROR_CODES.EMAIL_ALREADY_EXISTS) ||
    hasErrorCode(error, OTP_ERROR_CODES.USER_ALREADY_EXISTS)
  );
}

/**
 * Check if the error indicates rate limiting
 */
export function isRateLimited(error: unknown): boolean {
  return (
    hasErrorCode(error, OTP_ERROR_CODES.OTP_RATE_LIMIT_EXCEEDED) ||
    hasErrorCode(error, OTP_ERROR_CODES.OTP_IP_RATE_LIMIT_EXCEEDED)
  );
}

/**
 * Check if the error indicates the OTP code is invalid or expired
 */
export function isOTPInvalidOrExpired(error: unknown): boolean {
  return (
    hasErrorCode(error, OTP_ERROR_CODES.OTP_INVALID) ||
    hasErrorCode(error, OTP_ERROR_CODES.OTP_EXPIRED) ||
    hasErrorCode(error, OTP_ERROR_CODES.OTP_MAX_ATTEMPTS_EXCEEDED)
  );
}
